import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import {
  INTRO_ACCENT_COLOR,
  LOADER_DURATION_MS,
  LOADER_TICK_MS,
  anatomyV2IntroPNG,
  anatomyV2IntroWEBP,
} from '~/pages/anatomyV2/intro/libs/components/AnatomyV2Intro/constants'
import { AnatomyV2IntroStyles as S } from '~/pages/anatomyV2/intro/libs/components/AnatomyV2Intro/styles'
import { useMatchHeaderBackground } from '~/pages/anatomyV2/libs/hooks/useMatchHeaderBackground'
import { Progress } from '~/shared/components/Progress'
import { PageId } from '~/shared/constants/pagesConstants'
import { eventLogger } from '~/shared/services/eventLogger'
import { appHistory } from '~/shared/utils'

const QUESTION = 'You never learned Anatomy like this'

interface IAnatomyV2IntroProps {
  nextPagePath: string
}

const AnatomyV2Intro = ({ nextPagePath }: IAnatomyV2IntroProps) => {
  useMatchHeaderBackground()

  const [progressPercent, setProgressPercent] = useState(0)

  useEffect(() => {
    let elapsed = 0

    const timer = setInterval(() => {
      elapsed += LOADER_TICK_MS
      const percent = Math.min((elapsed / LOADER_DURATION_MS) * 100, 100)
      setProgressPercent(percent)

      if (elapsed >= LOADER_DURATION_MS) {
        clearInterval(timer)
        eventLogger.logFirstPageCompleted(QUESTION)
        eventLogger.logQuestion({
          question: QUESTION,
          pageName: PageId.INTRO_ANATOMY_V2_1,
        })
        appHistory.goTo(nextPagePath)
      }
    }, LOADER_TICK_MS)

    return () => clearInterval(timer)
  }, [nextPagePath])

  return (
    <S.Root>
      <S.ContentWrapper>
        <S.Title>
          <Trans
            ns="anatomyV2"
            i18nKey="onboarding.intro.title"
            components={{ span: <span /> }}
          />
        </S.Title>

        <S.Subtitle>
          <Trans ns="anatomyV2" i18nKey="onboarding.intro.subtitle" />
        </S.Subtitle>

        <S.Badges>
          <S.Badge>
            <Trans ns="anatomyV2" i18nKey="onboarding.intro.quizBadge" />
          </S.Badge>
          <S.Badge>
            <Trans ns="anatomyV2" i18nKey="onboarding.intro.ratingBadge" />
          </S.Badge>
        </S.Badges>

        <S.ImageWrapper>
          <S.Picture>
            <source srcSet={anatomyV2IntroWEBP} type="image/webp" />
            <S.Image src={anatomyV2IntroPNG} alt="Anatomy body parts" />
          </S.Picture>

          <S.LetterBadge $corner="a">A</S.LetterBadge>
          <S.LetterBadge $corner="b">B</S.LetterBadge>
          <S.LetterBadge $corner="c">C</S.LetterBadge>
          <S.LetterBadge $corner="d">D</S.LetterBadge>
        </S.ImageWrapper>

        <S.LoaderSection>
          <S.LoaderText>
            <Trans ns="anatomyV2" i18nKey="onboarding.intro.loaderText" />
          </S.LoaderText>

          <S.ProgressWrapper>
            <Progress
              color={INTRO_ACCENT_COLOR}
              $background="transparent"
              $valueBorderRadius="999px"
              $roundedAllSides
              value={progressPercent}
              max={100}
            />
          </S.ProgressWrapper>
        </S.LoaderSection>
      </S.ContentWrapper>
    </S.Root>
  )
}

export default AnatomyV2Intro
